<script lang="ts">
export type * from './SelectorDropdownTypes';
</script>

<script setup lang="ts" generic="T">
import type { Option, OptionGroup, OptionItem } from './SelectorDropdownTypes';
import type { OptionHTMLAttributes, SelectHTMLAttributes } from 'vue';
import IconChevron from '~theme/components/atoms/icons/IconChevron.vue';
import InputStyle from '~theme/components/atoms/styles/InputStyle.vue';

const props = withDefaults(
  defineProps<{
    name?: SelectHTMLAttributes['name'];
    ariaLabel?: SelectHTMLAttributes['aria-label'];
    invalid?: boolean;
    disabled?: boolean;
    modelValue: T;
    options: OptionItem<T>[];
    placeholder?: OptionHTMLAttributes['placeholder'];
  }>(),
  {
    name: undefined,
    invalid: false,
    disabled: false,
    modelValue: undefined,
    ariaLabel: undefined,
    placeholder: '',
  }
);

const emits = defineEmits<{
  'update:modelValue': [value: T];
  focus: [event: FocusEvent];
  blur: [event: FocusEvent];
}>();

const isOptionGroup = (item: OptionItem<T>): item is OptionGroup<T> => {
  return item.type === 'group';
};

const isEmpty = computed((): boolean => props.modelValue === undefined);

const flattenOptions = computed((): Option<T>[] =>
  props.options.reduce<Option<T>[]>((accumulator, item) => {
    if (isOptionGroup(item)) {
      return [...accumulator, ...item.values];
    }
    return [...accumulator, item];
  }, [])
);

const text = computed((): string => {
  if (isEmpty.value) {
    return props.placeholder;
  }
  const selectedOption = flattenOptions.value.find(
    (option: Option<T>): boolean => option.value === props.modelValue
  );
  return selectedOption ? selectedOption.title : props.placeholder;
});

const value = computed({
  get(): T {
    return props.modelValue;
  },
  set(value: T) {
    emits('update:modelValue', value);
  },
});
</script>

<template>
  <InputStyle :invalid="invalid">
    <div
      :class="[$style.selector, { [$style['selector--disabled']]: disabled }]"
    >
      <span
        :class="[
          $style.selector__text,
          { [$style['selector__text--placeholder']]: isEmpty },
        ]"
        >{{ text }}</span
      >
      <IconChevron
        direction="down"
        :class="[
          $style.selector__icon,
          {
            [$style['selector__icon--invalid']]: invalid,
            [$style['selector__icon--disabled']]: disabled,
          },
        ]"
      />
      <select
        v-model="value"
        :name="name"
        :disabled="disabled"
        :placeholder="placeholder"
        v-bind="$attrs"
        :class="$style.selector__select"
        :aria-label="ariaLabel"
        @focus="emits('focus', $event)"
        @blur="emits('blur', $event)"
      >
        <template v-for="(item, parentIndex) in options">
          <optgroup
            v-if="item.type === 'group'"
            :key="parentIndex"
            :label="item.title"
          >
            <option
              v-for="(option, childIndex) in item.values"
              :key="`${parentIndex}-${childIndex}`"
              :value="option.value"
              :label="option.title"
              :disabled="option.disabled"
              :hidden="option.hidden"
              :class="$style.option"
            >
              {{ option.title }}
            </option>
          </optgroup>
          <option
            v-if="item.type === 'item' || item.type == null"
            :key="parentIndex"
            :value="item.value"
            :label="item.title"
            :disabled="item.disabled"
            :hidden="item.hidden"
            :class="$style.option"
          >
            {{ item.title }}
          </option>
        </template>
      </select>
    </div>
  </InputStyle>
</template>

<style lang="scss" module>
.selector {
  $root: &;

  @include create-stacking-context;

  & {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    text-decoration: none;
  }

  &--disabled {
    background-color: $color-surface-tertiary;
  }

  &__text {
    &--placeholder {
      color: $color-text-and-object-disable;
    }
  }

  &__select {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: transparent;

    @include is-pointer-device {
      &:hover {
        cursor: pointer;
      }
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 12px;
    color: $color-text-and-object-medium-emphasis;

    &--invalid {
      color: $color-caution-accent;
    }

    &--disabled {
      color: $color-text-and-object-disable;
    }
  }
}

.option {
  color: $color-text-and-object-high-emphasis;
}
</style>
